import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Globe, Building2, FlaskConical, ChevronRight, MoreHorizontal,
  Pin, PinOff, RefreshCw, AlertTriangle, BadgeCheck, Clock,
} from "lucide-react";
import { useState } from "react";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import { type AdAccount, formatMoneyCents, formatRelative, formatDate } from "./types";

interface Props {
  account: AdAccount;
  onMutated: (next: AdAccount) => void;
}

export default function AdAccountCard({ account: a, onMutated }: Props) {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);

  const pin = useMutation({
    mutationFn: async () => {
      const method = a.pinned ? api.DELETE : api.POST;
      const { data, error, response } = await method(
        "/api/v1/ad-accounts/{id}/pin",
        { params: { path: { id: a.id } } }
      );
      if (error || !response.ok) throw error || new Error(a.pinned ? "取消置顶失败" : "置顶失败");
      return data as unknown as AdAccount;
    },
    onSuccess: (next) => {
      onMutated(next);
      qc.invalidateQueries({ queryKey: ["ad-accounts"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sync = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{id}/sync",
        { params: { path: { id: a.id } } }
      );
      if (error || !response.ok) throw error || new Error("同步失败");
      return data as unknown as AdAccount;
    },
    onSuccess: (next) => {
      onMutated(next);
      toast.success("已同步最新指标");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const isTemp = a.accessType === "TEMPORARY";
  const isPermDisplay = !isTemp;

  return (
    <article className="rounded-lg border border-border bg-background overflow-hidden flex flex-col">
      <div className="p-4 space-y-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {a.sandbox
              ? <FlaskConical className="w-4 h-4 text-amber-700 flex-shrink-0" />
              : <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" title={a.name}>{a.name}</div>
              <div className="font-mono text-xs text-muted-foreground truncate">
                ACT_{a.metaAccountId}
                {a.organizationName && <> · <span className="not-italic">{a.organizationName}</span></>}
              </div>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-md p-1 hover:bg-muted text-muted-foreground"
              aria-label="更多"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 min-w-[140px] rounded-md border border-border bg-background shadow-lg py-1">
                  <button
                    onClick={() => { setMenuOpen(false); pin.mutate(); }}
                    disabled={pin.isPending}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-50"
                  >
                    {a.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                    {a.pinned ? "取消置顶" : "置顶"}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); sync.mutate(); }}
                    disabled={sync.isPending}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={"w-3.5 h-3.5 " + (sync.isPending ? "animate-spin" : "")} />
                    {sync.isPending ? "同步中…" : "刷新"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          {isPermDisplay && (
            <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5">
              <BadgeCheck className="w-3 h-3" /> 永久
            </span>
          )}
          {isTemp && (
            <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-2 py-0.5">
              <Clock className="w-3 h-3" /> 临时 · {formatDate(a.grantExpiresAt)}
            </span>
          )}
          {a.sandbox && (
            <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-0.5">
              沙盒
            </span>
          )}
          {a.currency && (
            <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5">
              <Globe className="w-3 h-3" /> {a.currency}
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border">
          <Stat label="本月消耗" value={formatMoneyCents(a.cachedSpendMonthCents, a.cachedSpendCurrency ?? a.currency)} />
          <Stat label="营销活动" value={a.cachedCampaignCount?.toString() ?? "—"} />
          <Stat label="最近同步" value={formatRelative(a.lastSyncedAt)} />
        </div>

        <div className="text-xs text-muted-foreground">
          {a.timezoneName ?? "—"}
        </div>
      </div>

      {a.needsReauth ? (
        <div className="bg-rose-50 border-t border-rose-200 px-4 py-2 text-xs text-rose-900 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          token 已失效，请联系 OrgAdmin 重新授权
        </div>
      ) : (
        <Link
          to={`/ad-accounts/${a.id}`}
          className="border-t border-border px-4 py-2 text-xs text-primary hover:bg-muted flex items-center justify-between"
        >
          <span className="flex items-center gap-1">
            <Building2 className="w-3.5 h-3.5" /> 进入控制台
          </span>
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium truncate" title={value}>{value}</div>
    </div>
  );
}
